import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE } from "../types";

/**
 * Default master switch for the upstream-configulator-docs convention.
 * When no config is supplied, the convention ships **enabled** so every
 * project that consumes `@codedrifters/configulator` carries guidance on
 * how to read configulator's upstream docs and how to file feature
 * requests against `codedrifters/packages` instead of working around
 * gaps in downstream rule overrides.
 *
 * Non-CodeDrifters consumers that do not want to point downstream agents
 * at the `codedrifters/packages` repo can opt out by setting
 * `AgentConfigOptions.upstreamConfigulator.enabled = false`.
 *
 * @see UpstreamConfigulatorConfig
 */
export const DEFAULT_UPSTREAM_CONFIGULATOR_ENABLED = true;

/**
 * Concise summary rule body — renders into the consumer's top-level
 * `CLAUDE.md` so brevity matters. Cites the upstream repo, the docs
 * root, the most useful `gh` recipes, and the upstream-issue-first
 * preference, then defers to `upstream-configulator-docs-detail` for
 * the long-form reference.
 */
const UPSTREAM_CONFIGULATOR_DOCS_SUMMARY = [
  "# Upstream Configulator Docs",
  "",
  "This project consumes `@codedrifters/configulator` from the upstream repository **`codedrifters/packages`**. When you need to understand a configulator behaviour, project type, bundle, or rule, read the upstream docs first — the configulator package in this checkout is generated, not authoritative.",
  "",
  "## Where the docs live",
  "",
  "- **Repository:** `codedrifters/packages`",
  "- **Docs root:** `docs/src/content/docs/`",
  "- **Canonical absolute base URL:**",
  "  <https://github.com/codedrifters/packages/tree/main/docs/src/content/docs/>",
  "",
  "Always link to upstream docs from this repo's CLAUDE.md or `.claude/rules/` using **absolute** GitHub URLs — relative links only work inside a single repo's tree, not across repos.",
  "",
  "## Reading the docs without cloning",
  "",
  "Use the `gh` CLI to read upstream docs from inside this checkout — no clone of `codedrifters/packages` required:",
  "",
  "```sh",
  "# Read one doc by path:",
  "gh api repos/codedrifters/packages/contents/docs/src/content/docs/<path> \\",
  "  --jq '.content' | base64 -d",
  "",
  "# List a docs directory:",
  "gh api repos/codedrifters/packages/contents/docs/src/content/docs/<dir> \\",
  "  --jq '.[].path'",
  "",
  "# Search docs by keyword:",
  "gh search code --repo codedrifters/packages --filename '*.md' '<query>'",
  "```",
  "",
  "The Contents API returns base64-encoded payloads — `--jq '.content' | base64 -d` is the canonical decode recipe.",
  "",
  "## Upstream-issue-first preference",
  "",
  "When configulator is missing a feature, surfaces a bug, or behaves wrong, **file an issue against `codedrifters/packages`** rather than patching around it locally. Manual downstream overrides in `agentConfig.rules` or `agentConfig.ruleExtensions` are a last resort, not a first response — they fork divergence into every consumer that hits the same gap.",
  "",
  "Use the conventional issue recipe (mirrors the `Create Issue Workflow` in this project's CLAUDE.md):",
  "",
  "```sh",
  "gh issue create --repo codedrifters/packages \\",
  "  --title 'feat: <short description>' \\",
  "  --label 'type:feat' \\",
  "  --label 'priority:medium' \\",
  "  --label 'status:ready' \\",
  "  --body $'## Summary\\n\\n<1-3 sentences>\\n\\n## Details\\n\\n<context, acceptance criteria, or repro steps>'",
  "```",
  "",
  "Pick the `type:*` and matching prefix that describes the work (`feat:`, `fix:`, `chore:`, `docs:`, `refactor:`). Set `priority:*` from the urgency and use `status:blocked` instead of `status:ready` if the issue depends on another open issue.",
  "",
  "Then set the new issue's **GitHub issue type** — a separate field from the `type:*` label, which `gh issue create` cannot set. Derive it from the title prefix (`epic:` → Epic, `feat:` → Feature, `fix:` → Bug, everything else → Task). The local `.claude/procedures/set-issue-type.sh` helper resolves owner/repo from the **current** checkout, so a cross-repo filing needs the `updateIssueIssueType` GraphQL flow with an explicit owner/repo:",
  "",
  "```sh",
  "gh api graphql -f query='query($owner:String!,$repo:String!){repository(owner:$owner,name:$repo){issueTypes(first:50){nodes{id name}}}}' -f owner=codedrifters -f repo=packages",
  "gh issue view <issue-number> --repo codedrifters/packages --json id --jq '.id'",
  "gh api graphql -f query='mutation($issueId:ID!,$typeId:ID!){updateIssueIssueType(input:{issueId:$issueId,issueTypeId:$typeId}){issue{number issueType{name}}}}' -f issueId=<issue-node-id> -f typeId=<issue-type-node-id>",
  "```",
  "",
  "See `.claude/rules/upstream-configulator-docs-detail.md` for the full reference — extended `gh` recipes, common pitfalls (rate limits, base64 decoding, search vs. browse), and a complete issue-creation walkthrough.",
].join("\n");

/**
 * Detailed reference rule body — renders into
 * `.claude/rules/upstream-configulator-docs-detail.md` and only loads
 * when an agent is editing configulator-aware files (`.projenrc.ts`,
 * `projenrc/**`, `.claude/**`, `CLAUDE.md`). Carries the long-form
 * recipes, common pitfalls, and the full upstream-issue walkthrough.
 */
const UPSTREAM_CONFIGULATOR_DOCS_DETAIL = [
  "# Upstream Configulator Docs — Detail",
  "",
  "This project depends on `@codedrifters/configulator` for its Projen project types, agent bundles, and generated rules. Configulator itself is developed in the upstream repository **`codedrifters/packages`** — the source of record for project-type defaults, bundle behaviour, rule content, and the agent-config schema.",
  "",
  "When you are editing `.projenrc.ts`, `projenrc/**`, `.claude/**`, or `CLAUDE.md` and need to confirm how a configulator feature works, **read the upstream docs before editing**. Generated files in this checkout are not authoritative; the upstream markdown is.",
  "",
  "## 1. Where configulator's docs live",
  "",
  "- **Upstream repo:** `codedrifters/packages`",
  "- **Docs root inside the repo:** `docs/src/content/docs/`",
  "- **Canonical absolute base URL:**",
  "  <https://github.com/codedrifters/packages/tree/main/docs/src/content/docs/>",
  "",
  "Always cite upstream docs with **absolute** `https://github.com/codedrifters/packages/...` URLs when linking from this consumer's CLAUDE.md or `.claude/rules/`. Relative `../docs/...` links only resolve inside a single repo's tree — they break the moment a downstream agent loads the rule from a different checkout.",
  "",
  "Common doc subtrees (browse the canonical base above for the live tree):",
  "",
  "- `docs/src/content/docs/agents/` — agent bundles, phase contracts, issue templates",
  "- `docs/src/content/docs/components/` — Projen components and project types",
  "- `docs/src/content/docs/requirements/` — requirement documents and ADRs",
  "",
  "## 2. Reading the docs via the GitHub API",
  "",
  "Use `gh` to fetch upstream docs without cloning `codedrifters/packages`. The recipes below run from inside any consumer's checkout.",
  "",
  "### Browse a single doc by path",
  "",
  "```sh",
  "gh api repos/codedrifters/packages/contents/docs/src/content/docs/<path> \\",
  "  --jq '.content' | base64 -d",
  "```",
  "",
  'The GitHub Contents API returns `{ content: "<base64>", encoding: "base64", ... }`. The `--jq \'.content\'` filter strips the JSON envelope and `base64 -d` decodes the payload to plain Markdown. On macOS the decode flag is `base64 -D` on older `base64` versions — use `base64 -d` (lowercase) on the GNU coreutils install that ships with most modern dev environments.',
  "",
  "Example — fetch the agent issue-templates page:",
  "",
  "```sh",
  "gh api repos/codedrifters/packages/contents/docs/src/content/docs/agents/issue-templates.md \\",
  "  --jq '.content' | base64 -d",
  "```",
  "",
  "### List a docs directory",
  "",
  "```sh",
  "gh api repos/codedrifters/packages/contents/docs/src/content/docs/<dir> \\",
  "  --jq '.[].path'",
  "```",
  "",
  "Lists the immediate children (files and subdirectories) of `<dir>`. Use this to discover what's inside an agents/ or components/ subtree before browsing individual files.",
  "",
  "### Search docs by keyword",
  "",
  "```sh",
  "gh search code --repo codedrifters/packages --filename '*.md' '<query>'",
  "```",
  "",
  "`gh search code` hits the GitHub code-search index and is the right tool when you don't know which file to read. Scope by `--filename '*.md'` to keep results to documentation. Scope further with `--path 'docs/src/content/docs/<subtree>'` when you only care about one section.",
  "",
  "### Common pitfalls",
  "",
  "- **Forgot to base64-decode.** The Contents API never returns plain text — without `base64 -d` you get a base64 blob, not Markdown. Pipe through `base64 -d` every time.",
  "- **Rate limits.** Unauthenticated `gh` calls share a tight per-IP quota. Authenticate with `gh auth login` before running these recipes; an authenticated token gets a 5,000-request hourly quota that is more than enough for interactive doc browsing.",
  "- **Search vs. browse.** `gh search code` is best when you don't know the path. `gh api .../contents/...` is best when you do. Don't search for a path you already know — it's slower and the search index can lag behind the live file by a few minutes.",
  "- **Branch defaults.** All recipes default to the repo's default branch (`main`). To read a doc from a feature branch, append `?ref=<branch>` to the contents-API path: `gh api 'repos/codedrifters/packages/contents/<path>?ref=<branch>' --jq '.content' | base64 -d`.",
  "- **Large directories.** `gh api .../contents/<dir>` truncates responses over 1,000 entries. The `docs/src/content/docs/` subtrees are well under that limit today, but if you ever hit truncation, fall back to the Git Trees API: `gh api repos/codedrifters/packages/git/trees/main?recursive=1 --jq '.tree[] | select(.path | startswith(\"docs/src/content/docs/\")) | .path'`.",
  "",
  "## 3. Upstream-issue-first preference",
  "",
  "Configulator's rules, bundles, project types, and agent contracts ship for every consumer of the package. When you find a gap or a bug, the right fix is almost always upstream — patching around it locally forks divergence into this consumer and silently denies every other consumer the fix.",
  "",
  "**Default policy:** file an issue against `codedrifters/packages`. Reach for `agentConfig.rules` (new local rule) or `agentConfig.ruleExtensions` (append to a bundle rule) only when the upstream change is non-trivial and you need to unblock immediate work.",
  "",
  "### Canonical issue-creation recipe",
  "",
  "The recipe mirrors the `Create Issue Workflow` documented in this project's CLAUDE.md — same conventional-prefix titles, same `type:*` / `priority:*` / `status:*` label set, same body template — with `--repo codedrifters/packages` so it works from inside any consumer's checkout:",
  "",
  "```sh",
  "gh issue create --repo codedrifters/packages \\",
  "  --title '<type>: <short description>' \\",
  "  --label '<type-label>' \\",
  "  --label '<priority-label>' \\",
  "  --label '<status-label>' \\",
  "  --body $'## Summary\\n\\n<1-3 sentences describing the issue>\\n\\n## Details\\n\\n<acceptance criteria, repro steps, or context>'",
  "```",
  "",
  "### Title prefix → type label",
  "",
  "Pick exactly one matching pair:",
  "",
  "| Title prefix | `type:*` label | Use for |",
  "|--------------|----------------|---------|",
  "| `feat:` | `type:feat` | New features or functionality |",
  "| `fix:` | `type:fix` | Bug fixes |",
  "| `chore:` | `type:chore` | Maintenance: deps, tooling, config |",
  "| `docs:` | `type:docs` | Documentation-only work |",
  "| `refactor:` | `type:refactor` | Code restructure, no behavior change |",
  "| `release:` | `type:release` | Release preparation, version bumps |",
  "| `hotfix:` | `type:hotfix` | Urgent production fixes |",
  "| `epic:` | `type:feat` | Large initiatives spanning multiple child issues |",
  "",
  "### Priority and status labels",
  "",
  "- `priority:*` — pick exactly one of `priority:critical`, `priority:high`, `priority:medium`, `priority:low`, `priority:trivial`. Default to `priority:medium` when nothing in the request signals urgency. Use `priority:high` for blockers and `priority:critical` only for outages or release-blocking work.",
  "- `status:*` — `status:ready` for self-contained issues, `status:blocked` when the issue declares `Depends on: #<n>` against another open issue.",
  "",
  "### GitHub issue type",
  "",
  "The GitHub **issue type** (Epic / Feature / Bug / Task) is a separate field from the `type:*` label, and `gh issue create` cannot set it. Derive it from the title prefix — `epic:` → Epic, `feat:` → Feature, `fix:` → Bug, everything else → Task — and apply it in a follow-up call. The bundled `.claude/procedures/set-issue-type.sh` helper resolves owner/repo from the **current** checkout, so a cross-repo filing against `codedrifters/packages` uses the `updateIssueIssueType` GraphQL flow with an explicit owner/repo instead:",
  "",
  "```sh",
  "gh api graphql -f query='query($owner:String!,$repo:String!){repository(owner:$owner,name:$repo){issueTypes(first:50){nodes{id name}}}}' -f owner=codedrifters -f repo=packages",
  "gh issue view <issue-number> --repo codedrifters/packages --json id --jq '.id'",
  "gh api graphql -f query='mutation($issueId:ID!,$typeId:ID!){updateIssueIssueType(input:{issueId:$issueId,issueTypeId:$typeId}){issue{number issueType{name}}}}' -f issueId=<issue-node-id> -f typeId=<issue-type-node-id>",
  "```",
  "",
  "### Worked example",
  "",
  "Suppose configulator's `software-profile-analyst` agent prompt is missing a step you need. Instead of forking the prompt locally:",
  "",
  "```sh",
  "gh issue create --repo codedrifters/packages \\",
  "  --title 'feat: software-profile-analyst should record vendor pricing tier' \\",
  "  --label 'type:feat' \\",
  "  --label 'priority:medium' \\",
  "  --label 'status:ready' \\",
  "  --body $'## Summary\\n\\nThe `software-profile-analyst` Phase 2 prompt does not capture the vendor pricing tier (free / paid / enterprise), which downstream consumers need for segment matching.\\n\\n## Details\\n\\n- Add a `Pricing tier` field to the profile template at `docs/src/content/docs/agents/software-profile-analyst.md`\\n- Update the Phase 2 instructions to require the field before commit\\n- Add a regression test in `bundles.test.ts`'",
  "```",
  "",
  "The call above does **not** set the GitHub issue type — `gh issue create` cannot. Assign it immediately, in the same session, with the cross-repo GraphQL flow above (`feat:` title → **Feature**; every other prefix except `epic:` / `fix:` → **Task**). Skipping it leaves the upstream issue untyped, and nobody comes back for it later.",
  "",
  "After filing, reference the upstream issue from any local workaround so it can be reverted once the upstream fix lands:",
  "",
  "```typescript",
  "// TODO(codedrifters/packages#<N>): remove this override once upstream lands the fix.",
  "agentConfig: {",
  "  ruleExtensions: {",
  "    'software-profile-analyst': '## Local override\\n\\n- Capture pricing tier...',",
  "  },",
  "}",
  "```",
  "",
  "### When a local override is the right call",
  "",
  "Reach for `agentConfig.rules` or `agentConfig.ruleExtensions` only when **all** of the following hold:",
  "",
  "- The need is genuinely consumer-specific (not a gap that affects every configulator user).",
  "- An upstream fix would take longer than you can wait, **and** you have already filed the upstream issue with a `TODO(codedrifters/packages#<N>)` reference in the local override.",
  "- The override is small enough that diverging from upstream for a few days is cheaper than blocking on the upstream change.",
  "",
  "Anything that fails one of those tests belongs upstream first. The `# Reference Documentation` rule in this project's CLAUDE.md says it explicitly: trust the project sources and ask for clarification — that protocol extends across repo boundaries to `codedrifters/packages` for anything configulator owns.",
].join("\n");

/**
 * Upstream-configulator-docs bundle — always-on by default. Ships two
 * rules: an `ALWAYS`-scoped rule that renders into CLAUDE.md as a
 * top-level reminder that the project consumes
 * `@codedrifters/configulator` and where its upstream docs live, plus a
 * `FILE_PATTERN`-scoped detail rule that renders into
 * `.claude/rules/upstream-configulator-docs-detail.md` for agents
 * working on configulator-aware files.
 *
 * The bundle is gated by `AgentConfigOptions.upstreamConfigulator.enabled`
 * (default `true`). When the consumer flips it to `false`,
 * `AgentConfig.resolveRules` drops every rule whose name starts with
 * `upstream-configulator-docs` so non-CodeDrifters consumers that do
 * not want the upstream-pointer guidance opt out cleanly.
 */
export const upstreamConfigulatorDocsBundle: AgentRuleBundle = {
  name: "upstream-configulator-docs",
  description:
    "Pointers to @codedrifters/configulator's upstream docs and the upstream-issue protocol for missing features. Gated by AgentConfigOptions.upstreamConfigulator.enabled (default true).",
  appliesWhen: () => true,
  rules: [
    {
      name: "upstream-configulator-docs",
      description:
        "Top-level reminder that the project consumes @codedrifters/configulator and where the upstream docs live",
      scope: AGENT_RULE_SCOPE.ALWAYS,
      content: UPSTREAM_CONFIGULATOR_DOCS_SUMMARY,
      tags: ["project"],
    },
    {
      name: "upstream-configulator-docs-detail",
      description:
        "Detailed protocol for reading @codedrifters/configulator upstream docs and filing upstream issues",
      scope: AGENT_RULE_SCOPE.FILE_PATTERN,
      filePatterns: [".projenrc.ts", "projenrc/**", ".claude/**", "CLAUDE.md"],
      content: UPSTREAM_CONFIGULATOR_DOCS_DETAIL,
      tags: ["project"],
    },
  ],
};
